/**
 * The browser half of the language choice (see `src/shared/i18n.ts` for
 * what is translated and why).
 *
 * WHOSE CHOICE WINS. A visitor's own choice, then the business's default
 * for its page, then the browser's languages, then English. The
 * visitor's choice is remembered in their browser and nowhere else: a
 * public booking page has no account behind it, and a language
 * preference is not worth a cookie banner.
 *
 * A page that has not declared a business default renders in the
 * visitor's language as usual — `businessLocale` is optional precisely
 * so a screen can be wrapped before its data has loaded.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_LOCALE, intlLocale, isLocale, negotiateLocale, translator,
  type Locale, type Translate,
} from '@shared/i18n';

const STORAGE_KEY = 'assemble.locale';

/** A choice the visitor made earlier, if the browser kept it. */
export function storedLocale(): Locale | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

function remember(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Storage blocked: the choice lasts for this visit, which is better
    // than refusing to change language at all.
  }
}

/** What the browser asks for, as an Accept-Language header would say it. */
function browserAccept(): string {
  if (typeof window === 'undefined') return '';
  const tags = window.navigator.languages ?? [window.navigator.language];
  return tags
    .filter(Boolean)
    .map((tag, index) => `${tag};q=${Math.max(0.1, 1 - index * 0.1).toFixed(1)}`)
    .join(',');
}

/** The language for this visit, given what the business published. */
export function resolveLocale(businessLocale?: string | null): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const fromLink = new URLSearchParams(window.location.search).get('lang');
  return negotiateLocale({
    chosen: fromLink ?? storedLocale(),
    business: businessLocale ?? null,
    accept: browserAccept(),
  });
}

export interface I18n {
  locale: Locale;
  /** The tag for Intl: dates, times and money. */
  intl: string;
  t: Translate;
  /** Record a visitor's own choice. */
  choose: (locale: Locale) => void;
  /** True once the visitor has chosen, so the business default stops applying. */
  chosen: boolean;
}

const I18nContext = createContext<I18n | null>(null);

/**
 * Wraps a public screen. `businessLocale` is the page's own default and
 * may arrive later than the first render, which is why it is a prop
 * rather than something read once at start-up.
 */
export function I18nProvider({ businessLocale, children }: { businessLocale?: string | null; children: ReactNode }) {
  const [chosen, setChosen] = useState<Locale | null>(() => {
    if (typeof window === 'undefined') return null;
    const fromLink = new URLSearchParams(window.location.search).get('lang');
    return isLocale(fromLink) ? fromLink : storedLocale();
  });
  const locale = useMemo(() => (chosen ?? resolveLocale(businessLocale)), [chosen, businessLocale]);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const choose = useCallback((next: Locale) => {
    remember(next);
    setChosen(next);
  }, []);

  const value = useMemo<I18n>(() => ({
    locale,
    intl: intlLocale(locale),
    t: translator(locale),
    choose,
    chosen: chosen !== null,
  }), [locale, choose, chosen]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * The language of the screen being rendered. Outside a provider it
 * answers English rather than throwing: a component that is reused on an
 * untranslated screen should still render.
 */
export function useI18n(): I18n {
  const value = useContext(I18nContext);
  return value ?? FALLBACK;
}

const FALLBACK: I18n = {
  locale: DEFAULT_LOCALE,
  intl: intlLocale(DEFAULT_LOCALE),
  t: translator(DEFAULT_LOCALE),
  choose: () => undefined,
  chosen: false,
};
